/**
 * ArtistSetupPage - Create Artist Profile
 * Step 4: Artist vs Listener Fork - Setup artist profile with stage name, bio, genres
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Music, ArrowLeft, ArrowRight, Check, Mic2, Users,
  Instagram, Twitter, Youtube, Globe
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENRES = [
  'Hip-Hop', 'R&B', 'Pop', 'Rock', 'Country', 'Afrobeats',
  'Latin', 'Electronic', 'Jazz', 'Soul', 'Gospel', 'Indie'
];

const ArtistSetupPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [role, setRole] = useState('artist');
  const [stageName, setStageName] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    twitter: '',
    youtube: '',
    website: ''
  });

  const toggleGenre = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : prev.length < 3 ? [...prev, genre] : prev
    );
  };

  const handleSubmit = async () => {
    if (!stageName.trim()) {
      setError('Please enter your stage name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth');
        return;
      }

      const response = await axios.post(
        `${API_URL}/api/artists/profile/create`,
        {
          role,
          stage_name: stageName.trim(),
          bio: bio.trim(),
          location: location.trim(),
          genres: selectedGenres,
          social_links: socialLinks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Redirect to dashboard
        navigate('/artist-dashboard');
      }
    } catch (err) {
      console.error('Error creating profile:', err);
      // Extract error message from various formats
      let errorMessage = 'Failed to create profile. Please try again.';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || String(e)).join(', ');
        } else if (detail.msg) {
          errorMessage = detail.msg;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="p-4 flex items-center gap-4">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        
        {/* Progress Indicator */}
        <div className="flex-1 flex justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all ${
                s === step 
                  ? 'w-8 bg-gradient-to-r from-[#FF007F] to-[#00F0FF]' 
                  : s < step 
                    ? 'w-4 bg-[#FF007F]'
                    : 'w-4 bg-white/20'
              }`}
            />
          ))}
        </div>
        
        <div className="w-10" /> {/* Spacer for alignment */}
      </header>

      <main className="max-w-md mx-auto px-6 py-8 pb-24">
        {/* Step 1: Choose Role */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h1 
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: 'Unbounded' }}
            >
              What&apos;s your role?
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              Choose how you&apos;ll be creating on NLT Radio
            </p>

            <div className="space-y-4 mb-8">
              <button
                onClick={() => setRole('artist')}
                className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
                  role === 'artist'
                    ? 'border-[#FF007F] bg-[#FF007F]/10'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid="role-artist"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    role === 'artist' ? 'bg-[#FF007F]' : 'bg-white/10'
                  }`}>
                    <Mic2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Artist</h3>
                    <p className="text-[#71717A] text-sm">Upload complete songs to NoLies Radio</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setRole('producer')}
                className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
                  role === 'producer'
                    ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid="role-producer"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    role === 'producer' ? 'bg-[#00F0FF]' : 'bg-white/10'
                  }`}>
                    <Music className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Producer</h3>
                    <p className="text-[#71717A] text-sm">Showcase beats and instrumentals</p>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2"
              data-testid="next-step-btn-1"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Step 2: Basic Info */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h1 
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: 'Unbounded' }}
            >
              Tell us about yourself
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              This info will be shown on your profile
            </p>

            <div className="space-y-6 mb-8">
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">
                  Stage Name *
                </label>
                <input
                  type="text"
                  value={stageName}
                  onChange={(e) => setStageName(e.target.value)}
                  placeholder="Your artist or producer name"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors"
                  data-testid="stage-name-input"
                />
              </div>

              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">
                  Bio
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell fans about yourself and your music..."
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors resize-none"
                  data-testid="bio-input"
                />
              </div>

              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="City, State/Country"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors"
                  data-testid="location-input"
                />
              </div>

              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">
                  Genres (up to 3)
                </label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map((genre) => (
                    <button
                      key={genre}
                      onClick={() => toggleGenre(genre)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        selectedGenres.includes(genre)
                          ? 'bg-[#FF007F] text-white'
                          : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                      }`}
                      data-testid={`genre-${genre.toLowerCase()}`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm mb-4">{error}</p>
            )}

            <button
              onClick={() => setStep(3)}
              disabled={!stageName.trim()}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="next-step-btn-2"
            >
              Continue
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Step 3: Social Links & Confirm */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h1 
              className="text-2xl font-bold text-white mb-2"
              style={{ fontFamily: 'Unbounded' }}
            >
              Connect your socials
            </h1>
            <p className="text-[#A1A1AA] mb-8">
              Optional: Help fans find you everywhere
            </p>

            <div className="space-y-4 mb-8">
              <div className="relative">
                <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#E4405F]" />
                <input
                  type="text"
                  value={socialLinks.instagram}
                  onChange={(e) => setSocialLinks({ ...socialLinks, instagram: e.target.value })}
                  placeholder="Instagram username"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors"
                  data-testid="instagram-input"
                />
              </div>

              <div className="relative">
                <Twitter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#1DA1F2]" />
                <input
                  type="text"
                  value={socialLinks.twitter}
                  onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                  placeholder="Twitter/X username"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors"
                  data-testid="twitter-input"
                />
              </div>

              <div className="relative">
                <Youtube className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FF0000]" />
                <input
                  type="text"
                  value={socialLinks.youtube}
                  onChange={(e) => setSocialLinks({ ...socialLinks, youtube: e.target.value })}
                  placeholder="YouTube channel URL"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors"
                  data-testid="youtube-input"
                />
              </div>

              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                <input
                  type="text"
                  value={socialLinks.website}
                  onChange={(e) => setSocialLinks({ ...socialLinks, website: e.target.value })}
                  placeholder="Your website"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#FF007F] transition-colors"
                  data-testid="website-input"
                />
              </div>
            </div>

            {/* Profile Preview */}
            <div className="bg-white/5 rounded-2xl p-5 mb-6">
              <p className="text-[#71717A] text-sm mb-3">Profile Preview</p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
                  <span className="text-white font-bold text-xl">
                    {stageName.charAt(0).toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{stageName || 'Your Name'}</h3>
                  <p className="text-[#A1A1AA] text-sm capitalize">{role}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedGenres.map((genre) => (
                      <span key={genre} className="text-xs text-[#FF007F]">
                        #{genre}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm mb-4">{error}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={loading || !stageName.trim()}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="create-profile-btn"
            >
              {loading ? (
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Create Profile
                </>
              )}
            </button>

            <button
              onClick={() => navigate('/artist-dashboard')}
              className="w-full mt-4 py-3 text-[#71717A] hover:text-white transition-colors"
              data-testid="skip-btn"
            >
              Skip for now
            </button>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ArtistSetupPage;
